import React from "react";
import {
  styled,
  Pagination,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { format } from "date-fns";
import {
  DataGrid,
  useGridApiContext,
  GridColumnMenuContainer,
  SortGridMenuItems,
  GridFilterMenuItem,
  HideGridColMenuItem,
} from "@mui/x-data-grid";

import CustomCheckbox from "../CustomCheckbox";
import refreshIcon from "../../../assets/icons/refresh.svg";
import CustomIcon from "../CustomIcon";
import LinkButton from "../LinkButton";

const PaginationContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  padding: 15,
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiButtonBase-root": {
    color: "var(--gray-1)",
  },
}));

const CustomPagination = ({
  pageNumber,
  pageSize,
  totalItems,
  totalPages,
  updatedAt,
  onPaginationChange,
}) => {
  const apiRef = useGridApiContext();

  const handleChange = (event, value) => {
    apiRef.current.setPage(pageNumber);
    onPaginationChange({
      pageNumber: value,
    });
  };

  const handlePageSize = (event) => {
    onPaginationChange({
      pageSize: event.target.value || 10,
      pageNumber: 1,
    });
  };

  const handleRefresh = () => {
    onPaginationChange({
      pageNumber,
    });
  };

  return (
    <PaginationContainer>
      <div className="pagination--section footer--last-refreshed">
        <span>{`Last refreshed ${format(
          updatedAt,
          "MM-dd-yyyy hh:mm:ss"
        )}`}</span>

        <LinkButton
          startIcon={
            <span className="footer--refresh-icon">
              <CustomIcon svgIcon={refreshIcon} />
            </span>
          }
          onClick={handleRefresh}
        >
          Refresh
        </LinkButton>
      </div>
      <div className="pagination--section footer--pagination">
        <span>Show : </span>
        <FormControl size="small">
          <Select
            value={pageSize}
            sx={{ color: "var(--gray-1)" }}
            onChange={handlePageSize}
          >
            <MenuItem value={10} sx={{ color: "var(--gray-1)" }}>
              10
            </MenuItem>
            <MenuItem value={50} sx={{ color: "var(--gray-1)" }}>
              50
            </MenuItem>
            <MenuItem value={100} sx={{ color: "var(--gray-1)" }}>
              100
            </MenuItem>
          </Select>
        </FormControl>
        <span>{`of ${totalItems} Results `}</span>
        <StyledPagination
          count={totalPages}
          page={pageNumber}
          onChange={handleChange}
          variant="outlined"
          shape="rounded"
          size="large"
        />
      </div>
    </PaginationContainer>
  );
};

const CustomGridColumnMenu = React.forwardRef(function CustomGridColumnMenu(
  props,
  ref
) {
  const { handleColumnHide, handleShowColumns, ...rest } = props;
  const { hideMenu, currentColumn } = rest;

  return (
    <GridColumnMenuContainer ref={ref} {...rest}>
      <SortGridMenuItems onClick={hideMenu} column={currentColumn} />
      <GridFilterMenuItem onClick={hideMenu} column={currentColumn} />
      <HideGridColMenuItem
        onClick={(e) => {
          hideMenu(e);
          handleColumnHide(currentColumn.field);
        }}
        column={currentColumn}
      />
    </GridColumnMenuContainer>
  );
});

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  fontFamily: [
    "Roboto",
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(","),
  "& .MuiDataGrid-main": {
    border: "1px solid var(--gray-4)",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "var(--gray-6)",
    boxShadow: "inset 0 -1px 0 0 var(--gray-4)",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    color: "var(--gray-2)",
    fontSize: 14,
    fontWeight: 600,
  },
  "& .MuiDataGrid-cellContent": {
    color: "var(--secondary)",
    fontSize: 15,
  },
  "& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-footerContainer": {
    backgroundColor: "#fff",
    height: 64,
  },
}));

const CustomTable = ({
  rows,
  ApplayHeight = true,
  tableRef,
  onSelectionChange,
  columns,
  pageSize,
  pageNumber,
  totalItems,
  totalPages,
  loading,
  updatedAt,
  onPaginationChange,
  selectedRows,
  dataRef,
  DynamicHeight = () => {},
  onRowClick = () => {},
  handleColumnHide = () => {},
  handleShowColumns = () => {},
}) => {
  const topOffset = tableRef?.getBoundingClientRect().top;

  return (
    <div
      style={{
        height: ApplayHeight ? `calc(100vh - ${topOffset}px - 60px)` :  500 ,
        // width: "100%",
        scrollbarColor: "#6969dd #e0e0e0",
        scrollbarWidth: "thin",
        scrollbarGutter: "stable",
      }}
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        getRowHeight={DynamicHeight}
        autoHeight={ ApplayHeight && rows.length <= 10}
        getRowClassName={(params) => `datagrid-row`}
        components={{
          BaseCheckbox: CustomCheckbox,
          Pagination: CustomPagination,
          ColumnMenu: CustomGridColumnMenu,
        }}
        componentsProps={{
          pagination: {
            pageSize,
            totalItems,
            totalPages,
            pageNumber,
            updatedAt,
            onPaginationChange,
          },
          columnMenu: {
            handleColumnHide,
            handleShowColumns,
          },
        }}
        onRowClick={(selected) => onRowClick(selected.row)}
        onSelectionModelChange={onSelectionChange}
        selectionModel={selectedRows}
        loading={loading}
        ref={dataRef}
        checkboxSelection
        hideFooterRowCount
        hideFooterSelectedRowCount
        disableSelectionOnClick
      />
    </div>
  );
};

export default CustomTable;
